import { useContext, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ProjectTable, { SectionType } from "../ProjectTable";
import AppStateContext from "../../../contexts/AppStateContext";
import { ProductionVMType, ProductionsSummaryType } from "types/ProductionType";
import useStateWithLocalStorage from "hooks/UseStateWithLocalStorage";
import InitialsCircle from "components/InitalsCircle";
import { MachineType } from "types/MachineType";
import { apiGetMachines } from "util/network/Machine";
import SearchField from "components/SearchField";
import useStateWithSessionStorage from "hooks/UseStateWithSessionStorage";
import StatusBox from "components/Dashboard/StatusBox";
import {
  apiGetProduction,
  apiGetProductionCards,
  apiGetProductionsSummary,
  apiGetProductionsReport,
} from "util/network/Productions";
import ProductionTasks from "components/ProductionTasks/ProductionTasks";
import Modal from "components/Modals/Modal";
import AttemptCloseModal from "components/ProductionTasks/AttemptCloseModal";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import { useToast } from "contexts/ToastContext";

const styles = {
  inputField: {
    backgroundColor: "#fff",
    border: "1px solid #000",
    color: "#000",
    transition: "color 0.2s, border-color 0.2s",
    "&:hover": {
      borderColor: "#fff",
      color: "#fff !important",
    },
  },
  inputTitle: {
    "&:hover": {
      color: "#fff !important",
    },
  },
};

const ProjectManagementUI = () => {
  const { token, employees } = useContext(AppStateContext);
  const [production, setProduction] = useState<ProductionVMType>();
  const [newProdModal, setNewProdModal] = useState(false);
  const [searchValue, setSearchValue] = useStateWithSessionStorage<string>(
    "",
    "productionSearchVal"
  );
  const [refreshFunc, setRefreshFunc] = useState<() => void>(() => () => {});
  const { addToast } = useToast();
  const [selectedProductionIds, setSelectedProductionIds] = useState<number[]>(
    [],
  );
  const [printingCards, setPrintingCards] = useState(false);

  // The selection stays after printing, so a misprint can be sent again.
  const handlePrintCards = () => {
    if (selectedProductionIds.length === 0) return;
    setPrintingCards(true);
    apiGetProductionCards(token, selectedProductionIds)
      .catch(() => {
        addToast({
          type: "error",
          message: <>Could not print the production cards.</>,
        });
      })
      .finally(() => setPrintingCards(false));
  };

  const [tempSection, setTempSection] = useState<SectionType>({
    all: false,
    notStart: false,
    review: false,
    cam: false,
    production: false,
    postprocess: false,
    shipping: false,
    done: false,
    outsourcedFilter: null,
    custom: false,
  });

  const [section, setSection] = useStateWithLocalStorage<SectionType>(
    {
      all: false,
      notStart: false,
      review: false,
      cam: false,
      production: false,
      postprocess: false,
      shipping: false,
      done: false,
      outsourcedFilter: null,
      custom: false,
    },
    "productionFilter"
  );

  useEffect(() => {
    setTempSection((old) => {
      return {
        ...old,
        ...section,
      };
    });
  }, [section]);

  const [selectedUsers, setSelectedUsers] = useStateWithLocalStorage<number[]>(
    [],
    "productionUsers"
  );
  const [selectedMachines, setSelectedMachines] = useStateWithLocalStorage<
    number[]
  >([], "productionMachines");
  const [machines, setMachines] = useState<MachineType[]>([]);
  const [productionSummary, setProductionSummary] =
    useState<ProductionsSummaryType>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (production?.productId) {
      apiGetProduction(token, production?.productId).then((production) => {
        setProduction(production);
      });
    }
  }, [token, production?.productId]);

  useEffect(() => {
    apiGetMachines(token).then((res) => {
      setMachines(res);
    });
  }, [token]);

  const handleSectionChange = (event: any) => {
    // If 'all' is checked we make sure to uncheck everything else.
    // We should also uncheck all if anything else is checked.
    // Lastly if we uncheck the last box that is not 'all' then we should check 'all'
    if (event.target.name === "all") {
      if (event.target.checked) {
        setTempSection((prev) => ({
          ...prev,
          all: true,
          notStart: false,
          review: false,
          cam: false,
          production: false,
          postprocess: false,
          shipping: false,
          custom: false,
        }));
      } else {
        setTempSection((prev) => ({
          ...prev,
          all: false,
        }));
      }
    } else {
      if (event.target.checked) {
        setTempSection({
          ...tempSection,
          all: !["outsourced", "done", "all"].includes(event.target.name)
            ? false
            : true,
          [event.target.name]: true,
        });
      } else {
        setTempSection({
          ...tempSection,
          [event.target.name]: false,
        });
      }
    }
  };

  // Cycles all -> not outsourced -> outsourced -> all. Staged in tempSection
  // like the other filters, so it only applies on Apply.
  const changeOutsourcingFilter = () => {
    setTempSection((prev) => ({
      ...prev,
      outsourcedFilter:
        prev.outsourcedFilter == null ? false : prev.outsourcedFilter ? null : true,
    }));
  };

  useEffect(() => {
    // Periodically check every minute for new quotes
    if (token) {
      apiGetProductionsSummary(token).then((res) => {
        setProductionSummary(res);
      });
    }
    const interval = setInterval(() => {
      if (token) {
        apiGetProductionsSummary(token).then((res) => {
          setProductionSummary(res);
        });
      }
    }, 900000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <Grid container>

      {/* Header */}
      <Grid item xs={12}>
        <div
          style={{
            // minWidth: 120,
            paddingTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            background: "var(--header)",
            color: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              paddingRight: "10px",
            }}
          >
            <Typography
              fontWeight={"bold"}
              variant={"h6"}
              style={{
                textTransform: "uppercase",
                color: "#fff",
                paddingLeft: "1rem",
              }}
            >
              &nbsp;Production - overview &nbsp;
            </Typography>
          </div>
          <div
            style={{
              display: "flex",
              paddingRight: "2rem",
              justifyContent: "center",
            }}
          >
            <div style={{ marginRight: "1em", paddingBottom: "10px", display: "flex", gap: "0.5rem" }}>
              <Button
                onClick={() => setNewProdModal(true)}
                variant="contained"
                sx={{
                  borderRadius: 0,
                  maxHeight: 40,
                  width: "10rem",
                  background: "#E0A900",
                  lineHeight: 2,
                  color: "#fff",
                  "&:hover": {
                    color: "#E0A900",
                    border: "1px solid #E0A900",
                    background: "transparent",
                  },
                }}
              >
                + Production
              </Button>
              <Button
                onClick={() => apiGetProductionsReport(token)}
                variant="contained"
                startIcon={<FileDownloadIcon />}
                sx={{
                  borderRadius: 0,
                  maxHeight: 40,
                  background: "#E0A900",
                  lineHeight: 2,
                  color: "#fff",
                  "&:hover": {
                    color: "#E0A900",
                    border: "1px solid #E0A900",
                    background: "transparent",
                  },
                }}
              >
                Download Report
              </Button>
              <Button
                onClick={handlePrintCards}
                disabled={selectedProductionIds.length === 0 || printingCards}
                variant="contained"
                startIcon={<PrintIcon />}
                title={
                  selectedProductionIds.length === 0
                    ? "Tick parts in the table to print their production cards"
                    : undefined
                }
                sx={{
                  borderRadius: 0,
                  maxHeight: 40,
                  background: "#E0A900",
                  lineHeight: 2,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  // Fixed width and digits, so the count changing as rows
                  // are ticked does not shift the header around.
                  width: "13rem",
                  fontVariantNumeric: "tabular-nums",
                  "&:hover": {
                    color: "#E0A900",
                    border: "1px solid #E0A900",
                    background: "transparent",
                  },
                  "&.Mui-disabled": {
                    background: "#E0A900",
                    color: "#fff",
                    opacity: 0.5,
                  },
                }}
              >
                {selectedProductionIds.length === 0
                  ? "Print Cards"
                  : `Print ${selectedProductionIds.length} Card${
                      selectedProductionIds.length === 1 ? "" : "s"
                    }`}
              </Button>
              {showConfirmDialog && (
                <AttemptCloseModal
                  isOpen={showConfirmDialog}
                  onClose={() => setShowConfirmDialog(false)}
                  onConfirm={() => setNewProdModal(false)}
                />
              )}
              {newProdModal && (
                <Modal
                  isOpen={newProdModal}
                  onClose={() => {
                    setShowConfirmDialog(true);
                  }}
                  productionTasks
                >
                  <ProductionTasks
                    onClose={() => setNewProdModal(false)}
                    isOpen={newProdModal}
                    onSave={() => {
                      setNewProdModal(false);
                    }}
                  />
                </Modal>
              )}
            </div>
            <div style={{ marginRight: "1em" }}>
              {/* EE-264: the backend search now covers PRODUCTS.name, so the
                  model name printed on every row is searchable. `production`
                  help wording, because rows here are parts, not orders. */}
              <SearchField
                onChange={setSearchValue}
                value={searchValue}
                placeholder="Search model, part or order - press Enter"
                help="production"
              />
            </div>
          </div>
        </div>
      </Grid>
      
      {/* EE-237: full width since the Workload calendar that used to occupy
          the other half was removed. */}
      <Grid item xs={12}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "1rem 1.5rem 0 1.5rem",
        }}
      >

          {/* Status boxes. EE-264: grouped at the left rather than
              `space-between`, which stretched six boxes across the whole
              width once EE-237 removed the Workload calendar next to them.
              They wrap instead of shrinking on a narrow window. */}
          <Grid item xs={12}
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "flex-start",
              padding: "1rem",
            }}
          >
                <StatusBox
                  title={"All"}
                  amount={productionSummary?.totalActive}
                  smallText="Tasks"
                  checked={tempSection.all}
                  onChange={handleSectionChange}
                  name={"all"}
                />
                <StatusBox
                  title={"Review"}
                  amount={productionSummary?.reviewReady}
                  smallText="Tasks Ready"
                  checked={tempSection.review}
                  onChange={handleSectionChange}
                  name={"review"}
                />
                <StatusBox
                  title={"CAM"}
                  amount={productionSummary?.camReady}
                  smallText="Tasks Ready"
                  checked={tempSection.cam}
                  onChange={handleSectionChange}
                  name={"cam"}
                />
                <StatusBox
                  title={"Production"}
                  amount={productionSummary?.productionReady}
                  smallText="Tasks Ready"
                  checked={tempSection.production}
                  onChange={handleSectionChange}
                  name={"production"}
                />
                <StatusBox
                  title={"Afterwork"}
                  amount={productionSummary?.afterworkReady}
                  smallText="Tasks Ready"
                  checked={tempSection.postprocess}
                  onChange={handleSectionChange}
                  name={"postprocess"}
                />
                <StatusBox
                  title={"Ship"}
                  amount={productionSummary?.shippingReady}
                  smallText="Tasks Ready"
                  checked={tempSection.shipping}
                  onChange={handleSectionChange}
                  name={"shipping"}
                />
          </Grid>

          {/* Filtering options */}
          <Grid item xs={12}
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              padding: "1rem",
              alignItems: "flex-end",
            }}
          >
              <FormControlLabel
                label="Done"
                control={
                  <Checkbox
                    checked={tempSection.done}
                    onChange={handleSectionChange}
                    name={"done"}
                  />
                }
              />
              <FormControlLabel
                label="Outsourced"
                control={
                  <Checkbox
                    checked={tempSection.outsourcedFilter === true}
                    indeterminate={tempSection.outsourcedFilter == null}
                    onChange={changeOutsourcingFilter}
                    name={"outsourced"}
                  />
                }
              />
              <button
                onClick={() => {
                  setSection({
                    ...tempSection,
                  });
                }}
                style={{
                  padding: "8px 16px",
                  background: "var(--mainYellow)",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "16px",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                }}
              >
                Apply
              </button>
          </Grid>
        </Grid>

      <Grid item xs={12} sx={{ padding: "1.5rem" }}>
        <ProjectTable
          selectedUsers={selectedUsers}
          selectedMachines={selectedMachines}
          section={section}
          machinesIn={machines}
          searchValue={searchValue}
          setRefreshFunc={setRefreshFunc}
          onSelectionChange={setSelectedProductionIds}
        />
      </Grid>
    </Grid>
  );
};

export default ProjectManagementUI;
